import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ttAPI from "../../api/ttAPI";

export default function SchedulingChecklist({
    siteId,
    customerId,
    assetCoverageYearMonth,
    jobId = null,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function loadChecklist() {
      if (!siteId || !customerId || !assetCoverageYearMonth) {
        setData(null);
        return;
      }

      setLoading(true);
      const response = await ttAPI.getSchedulingChecklist(
        siteId,
        customerId,
        assetCoverageYearMonth,
        jobId
      );
      setData(response);
      setLoading(false);
    }

    loadChecklist();
  }, [siteId, customerId, assetCoverageYearMonth, jobId]);

  const updateItem = async (item, checked) => {
      setData(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          checklist: prev.checklist.map(x =>
            x.template_id === item.template_id
              ? { ...x, is_completed: checked }
              : x
          )
        };
      });

      await ttAPI.saveSchedulingChecklistItem({
        site_id: siteId,
        job_id: jobId,
        template_key: item.template_key,
        asset_coverage_year_month: assetCoverageYearMonth,
        is_completed: checked,
      });
    };

  if (!assetCoverageYearMonth) return null;

  const items = data?.checklist ?? [];
  const outstanding = data ? items.filter(x => !x.is_completed).length : 0;
  const fe = data?.fire_extinguishers;
  const hasFireWarning = fe && fe.replacement_required_count > 0;
  const highlight = hasFireWarning;

  return (
    <Box
      style={{
        marginBottom: 12,
        padding: "8px 12px",
        border: "1px solid #ddd",
        borderRadius: 4,
        background: "#FFFBE6",
        border: highlight ? "1px solid #F4D35E" : "1px solid #ddd",
        borderLeft: highlight ? "5px solid #FBC02D" : "1px solid #ddd",
      }}
    >
    <Box
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        rowGap: 6
      }}
      >
      <Box
        display="flex"
        alignItems="center"
        style={{
          cursor: "pointer",
          flex: 1,
          minWidth: 0
        }}
        onClick={() => setExpanded(!expanded)}
        >
        <Typography
          style={{
            marginRight: 8,
            fontWeight: 600,
          }}
        >
          {expanded ? "▼" : "▶"}
        </Typography>

        <Typography variant="body2" style={{ marginRight: 16 }}>
          Scheduling Checklist
        </Typography>

        {assetCoverageYearMonth !== "1900-01" && (
          <Typography variant="body2">
            <strong>Assets Due Through:</strong> {assetCoverageYearMonth}
          </Typography>
        )}
        </Box>

        <Typography
          variant="body2"
          style={{
            color: hasFireWarning ? "#d32f2f" : "#666",
            fontWeight: hasFireWarning ? 600 : 400,
            whiteSpace: "nowrap",
            marginLeft: "auto"
          }}
        >
          {loading ? "Loading..." : hasFireWarning
            ? `⚠ ${fe.replacement_required_count} FE replacement${fe.replacement_required_count === 1 ? "" : "s"}`
            : outstanding > 0
              ? `${outstanding} outstanding`
              : "All checked"}
        </Typography>
    </Box>

      <Collapse in={expanded}>
        <Box mt={1}>
          {loading && <CircularProgress size={20} />}

          {!loading && data && items.length === 0 && (
            <Typography variant="body2" style={{ color: "#777" }}>
              No checklist items for this month.
            </Typography>
          )}

          {!loading && items.map(item => (
            <FormControlLabel
              style={{
                display: "block",
                margin: 0
              }}
              key={item.template_id}
              control={
                <Checkbox
                  checked={!!item.is_completed}
                  onChange={(e) => updateItem(item, e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Tooltip title={item.details || ""} placement="top">
                  <span>{item.label || item.title}</span>
                </Tooltip>
              }
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
